// src/admin/pages/import-orders/ImportOrderFormPage.tsx
//
// Replaces ImportOrderCreateDialog.tsx / ImportOrderEditDialog.tsx (both were
// MUI Drawers) with a single full-page component shared by both the
// /admin/import-order/new and /admin/import-order/:id/edit routes — mode is
// inferred from the presence of the :id route param, following the same
// page-over-modal pattern already used by BulkCarUploadPage.tsx.
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, TextField, Grid, Typography, IconButton, Alert, CircularProgress,
  Autocomplete, Paper, Stack, Divider, Backdrop, LinearProgress, MenuItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, Save as SaveIcon, Cancel as CancelIcon,
  Add as AddIcon, Delete as DeleteIcon, UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import SupplierDialog from '../supplier/SupplierDialog';
import { ImportOrderService } from './importOrderService';
import {
  Supplier, ImportOrderItem, ImportOrderStatus,
  LcStatus, ImportOrderDocumentType, ImportOrderDocument,
  ImportOrderCost, CostAmountType, computeCostAmount,
} from './ImportOrder';
import ImportOrderItemsEditor, { validateImportOrderItems } from './ImportOrderItemsEditor';
import { useImportOrder, PO_KEYS } from '../../../hooks/useImportOrders';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { getCurrencySymbol } from '../../../util/productUtils';
import { useCompany } from '../../../contexts/CompanyContext';
import { formatCurrency } from '../../../util/Formatcurrency';
import ToastNotification from '../common/Toastnotification';

const DOCUMENT_TYPES: ImportOrderDocumentType[] = [
  'LC Copy', 'Invoice', 'Bill of Lading', 'Customs Declaration', 'Other',
];

const LC_STATUSES: LcStatus[] = ['Not Opened', 'Opened', 'Closed'];

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const ImportOrderFormPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { companyProfile } = useCompany();

  const companyCurrency = companyProfile?.currency || 'LKR';
  const currencySymbol = getCurrencySymbol(companyCurrency);

  const { data: existingOrder, isLoading: orderLoading } = useImportOrder(id);
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });
  const showToast = (message: string, severity: ToastState['severity']) => setToast({ open: true, message, severity });

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [items, setItems] = useState<ImportOrderItem[]>([]);
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);

  // ── Flexible landed costs — each line is a flat amount or a % of subtotal ──
  const [costs, setCosts] = useState<ImportOrderCost[]>([]);

  // ── Letter of Credit ──
  const [lcNumber, setLcNumber] = useState('');
  const [lcStatus, setLcStatus] = useState<LcStatus>('Not Opened');
  const [lcOpenedDate, setLcOpenedDate] = useState('');

  // ── Documents ──
  const [documentFiles, setDocumentFiles] = useState<{ file: File; type: ImportOrderDocumentType }[]>([]);
  const [existingDocuments, setExistingDocuments] = useState<ImportOrderDocument[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const initialized = useRef(false);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  // Pre-populate form from the loaded order once (edit mode only).
  useEffect(() => {
    if (!isEdit || !existingOrder || initialized.current) return;
    initialized.current = true;

    setSelectedSupplier({
      id: existingOrder.supplierId,
      name: existingOrder.supplierName,
      email: existingOrder.supplierEmail,
      phone: existingOrder.supplierPhone,
      address: '',
      isActive: true,
    });
    setItems(existingOrder.items.map(item => ({ ...item })));
    setExpectedDeliveryDate(
      existingOrder.expectedDeliveryDate
        ? new Date(existingOrder.expectedDeliveryDate).toISOString().split('T')[0]
        : ''
    );
    setNotes(existingOrder.notes || '');
    setInternalNotes(existingOrder.internalNotes || '');

    // Orders created before the cost rework store their landed costs across
    // separate taxRate/shippingCost/cifValue/customsDuty/vatAmount/luxuryTax/
    // otherTaxes fields instead of the current flexible `costs` list — migrate
    // them into equivalent cost lines the first time such an order is edited,
    // so the total isn't silently lost.
    if (existingOrder.costs && existingOrder.costs.length > 0) {
      setCosts(existingOrder.costs);
    } else {
      const migrated: ImportOrderCost[] = [];
      if (existingOrder.taxRate) migrated.push({ id: 'legacy-tax', name: 'Tax', amountType: 'percentage', value: existingOrder.taxRate });
      if (existingOrder.shippingCost) migrated.push({ id: 'legacy-shipping', name: 'Shipping', amountType: 'fixed', value: existingOrder.shippingCost });
      if (existingOrder.cifValue) migrated.push({ id: 'legacy-cif', name: 'CIF Value', amountType: 'fixed', value: existingOrder.cifValue });
      if (existingOrder.customsDuty) migrated.push({ id: 'legacy-customs', name: 'Customs Duty', amountType: 'fixed', value: existingOrder.customsDuty });
      if (existingOrder.vatAmount) migrated.push({ id: 'legacy-vat', name: 'VAT', amountType: 'fixed', value: existingOrder.vatAmount });
      if (existingOrder.luxuryTax) migrated.push({ id: 'legacy-luxury', name: 'Luxury Tax', amountType: 'fixed', value: existingOrder.luxuryTax });
      (existingOrder.otherTaxes || []).forEach(t => migrated.push({ id: t.id, name: t.name, amountType: 'fixed', value: t.amount }));
      setCosts(migrated);
    }

    setLcNumber(existingOrder.lcNumber || '');
    setLcStatus(existingOrder.lcStatus || 'Not Opened');
    setLcOpenedDate(
      existingOrder.lcOpenedDate ? new Date(existingOrder.lcOpenedDate).toISOString().split('T')[0] : ''
    );
    setExistingDocuments(existingOrder.documents || []);
  }, [isEdit, existingOrder]);

  const fetchSuppliers = async () => {
    try {
      const data = await ImportOrderService.getAllSuppliers();
      setSuppliers(data);
    } catch (err: any) {
      showToast('Failed to load suppliers', 'error');
    }
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + item.totalPrice, 0);
    const costsTotal = costs.reduce((sum, c) => sum + computeCostAmount(c, subtotal), 0);
    const total = subtotal + costsTotal;
    return { subtotal, costsTotal, total };
  };

  // ── Costs editor ──
  const addCost = () => setCosts(prev => [...prev, { id: `cost-${Date.now()}`, name: '', amountType: 'fixed', value: 0 }]);
  const removeCost = (id: string) => setCosts(prev => prev.filter(c => c.id !== id));
  const updateCost = (id: string, patch: Partial<ImportOrderCost>) =>
    setCosts(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));

  // ── Documents ──
  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setDocumentFiles(prev => [...prev, ...files.map(file => ({ file, type: 'Other' as ImportOrderDocumentType }))]);
    e.target.value = '';
  };
  const removeStagedDocument = (index: number) => setDocumentFiles(prev => prev.filter((_, i) => i !== index));
  const updateStagedDocumentType = (index: number, type: ImportOrderDocumentType) =>
    setDocumentFiles(prev => prev.map((d, i) => i === index ? { ...d, type } : d));

  const handleSubmit = async () => {
    if (!currentUser) { showToast(`You must be logged in to ${isEdit ? 'update' : 'create'} import orders`, 'error'); return; }
    if (!selectedSupplier) { setError('Please select a supplier'); showToast('Please select a supplier', 'error'); return; }

    const itemsError = validateImportOrderItems(items);
    if (itemsError) { setError(itemsError); showToast(itemsError, 'error'); return; }

    for (const c of costs) {
      if (!c.name.trim()) { setError('Please name every cost row (or remove it)'); showToast('Please name every cost row (or remove it)', 'error'); return; }
      if (c.value < 0) { setError('Cost amounts cannot be negative'); showToast('Cost amounts cannot be negative', 'error'); return; }
      if (c.amountType === 'percentage' && c.value > 100) { setError('A percentage cost cannot exceed 100%'); showToast('A percentage cost cannot exceed 100%', 'error'); return; }
    }

    const formData = {
      supplierId: selectedSupplier.id!,
      items, costs,
      expectedDeliveryDate: expectedDeliveryDate ? new Date(expectedDeliveryDate) : null,
      notes, internalNotes,
      lcNumber, lcStatus,
      lcOpenedDate: lcOpenedDate ? new Date(lcOpenedDate) : null,
    };

    try {
      setLoading(true);
      setError('');

      let poId: string;
      if (isEdit) {
        poId = id!;
        await ImportOrderService.updateImportOrder(poId, formData);
      } else {
        poId = await ImportOrderService.createImportOrder(
          formData, currentUser.uid, currentUser.displayName || currentUser.email || 'Unknown'
        );
      }

      if (documentFiles.length > 0) {
        await ImportOrderService.uploadImportOrderDocuments(poId, documentFiles);
      }

      // The create/update calls above go straight through ImportOrderService
      // rather than the usePOMutations() hook, so its onSuccess invalidation
      // never runs — invalidate here instead, so the list/stats the user
      // lands on next don't show stale cached data.
      queryClient.invalidateQueries({ queryKey: PO_KEYS.all });

      showToast(`Import order ${isEdit ? 'updated' : 'created'} successfully`, 'success');
      // `replace: true` swaps this create/edit form out of history instead of
      // pushing on top of it — otherwise a Back button would land back on
      // this now-stale form instead of wherever it was actually reached from.
      if (isEdit) {
        navigate(`/admin/import-order/${poId}`, { replace: true });
      } else {
        // A freshly-created order goes straight to the table view so staff
        // can immediately see it in context, not the single-order detail page.
        navigate('/admin/import-order', { replace: true });
      }
    } catch (err: any) {
      const errorMessage = err.message || `Failed to ${isEdit ? 'update' : 'create'} import order`;
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSupplierDialogSuccess = async () => {
    await fetchSuppliers();
    setSupplierDialogOpen(false);
    showToast('Supplier created successfully', 'success');
  };

  const totals = calculateTotals();

  // ── Edit mode: block editing once the order has left DRAFT ──
  // Protects direct/bookmarked URLs — the list page's Edit icon is already
  // gated the same way, but a raw URL hit has no such gate.
  if (isEdit && orderLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" py={8}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>Loading order…</Typography>
      </Box>
    );
  }

  if (isEdit && existingOrder && existingOrder.status !== ImportOrderStatus.DRAFT) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 700, mx: 'auto' }}>
        <Alert severity="warning" sx={{ mb: 2 }}>This order can no longer be edited — it has left Draft status.</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/admin/import-order/${id}`)}>
          Back to Order
        </Button>
      </Box>
    );
  }

  if (isEdit && !orderLoading && !existingOrder) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 700, mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>Import order not found.</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/import-order')}>Back to List</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', flex: 1, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header — fixed, does not scroll. Full-width outer shell so the
          scrollable body below can span the true viewport edge (scrollbar
          fix); this header's own content is centered via the inner maxWidth. */}
      <Box sx={{ flexShrink: 0, px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto', width: '100%' }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <IconButton onClick={() => navigate(-1)} aria-label="Back">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {isEdit ? 'Edit Import Order' : 'Create Import Order'}
            </Typography>
            {isEdit && existingOrder && (
              <Typography variant="body2" color="text.secondary">{existingOrder.poNumber}</Typography>
            )}
          </Box>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      </Box>

      {/* Scrollable form content — this Box spans the full viewport width so
          its native scrollbar renders at the true right edge instead of
          being inset by the maxWidth-centered layout (previously the whole
          page, including the scroll container, was capped at maxWidth:1400
          and centered, so on wide screens the scrollbar sat mid-page). The
          content itself is re-centered via the inner maxWidth wrapper. */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <Box sx={{ px: { xs: 2, md: 3 }, pb: 2, maxWidth: 1400, mx: 'auto', width: '100%' }}>
      <Grid container spacing={3}>
        {/* Supplier */}
        <Grid item xs={12}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
            <Autocomplete
              options={suppliers}
              getOptionLabel={(option) => option.name}
              value={selectedSupplier}
              onChange={(_, value) => setSelectedSupplier(value)}
              renderInput={(params) => <TextField {...params} label="Supplier *" placeholder="Select supplier" />}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{option.email} | {option.phone}</Typography>
                  </Box>
                </li>
              )}
              sx={{ flex: 1 }}
              disabled={loading}
            />
            <Button variant="outlined" onClick={() => setSupplierDialogOpen(true)} sx={{ minWidth: 120, height: { sm: 56 }, width: { xs: '100%', sm: 'auto' } }} disabled={loading}>
              New Supplier
            </Button>
          </Stack>
        </Grid>

        {/* Items */}
        <Grid item xs={12}>
          <ImportOrderItemsEditor items={items} setItems={setItems} currencySymbol={currencySymbol} disabled={loading} />
        </Grid>

        {/* Costs — a flexible list instead of fixed tax/shipping/CIF/customs/
            VAT/luxury-tax fields; each line is either a flat amount or a
            percentage of the items subtotal. */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Costs</Typography>
                <Typography variant="caption" color="text.secondary">
                  Shipping, customs duty, VAT, tax, or anything else that adds to the landed cost.
                </Typography>
              </Box>
              <Button size="small" startIcon={<AddIcon />} onClick={addCost} disabled={loading} variant="outlined">
                Add Cost
              </Button>
            </Stack>

            {costs.length === 0 ? (
              <Box sx={{ py: 2.5, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  No additional costs yet — the total is just the items subtotal.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.25}>
                {costs.map((c) => (
                  <Stack key={c.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <TextField
                      size="small" label="Name" placeholder="e.g. Shipping, Customs Duty, Insurance"
                      value={c.name} sx={{ flex: 2 }}
                      onChange={(e) => updateCost(c.id, { name: e.target.value })} disabled={loading}
                    />
                    <TextField
                      size="small" select label="Type" value={c.amountType} sx={{ flex: 1, minWidth: 130 }}
                      onChange={(e) => updateCost(c.id, { amountType: e.target.value as CostAmountType })} disabled={loading}
                    >
                      <MenuItem value="fixed">Fixed Amount</MenuItem>
                      <MenuItem value="percentage">% of Subtotal</MenuItem>
                    </TextField>
                    <TextField
                      size="small" type="number" sx={{ flex: 1 }}
                      label={c.amountType === 'percentage' ? 'Percentage (%)' : `Amount (${currencySymbol})`}
                      value={c.value}
                      onChange={(e) => updateCost(c.id, { value: parseFloat(e.target.value) || 0 })}
                      inputProps={c.amountType === 'percentage' ? { min: 0, max: 100, step: 0.01 } : { min: 0, step: 0.01 }}
                      disabled={loading}
                    />
                    <Typography variant="body2" sx={{ minWidth: 110, textAlign: { xs: 'left', sm: 'right' }, color: 'text.secondary' }}>
                      {formatCurrency(computeCostAmount(c, totals.subtotal), currencySymbol)}
                    </Typography>
                    <IconButton size="small" color="error" onClick={() => removeCost(c.id)} disabled={loading}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* Totals */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper', backgroundImage: 'none' }}>
            <Grid container spacing={1}>
              <Grid item xs={6}><Typography variant="body2" color="text.secondary">Items Subtotal:</Typography></Grid>
              <Grid item xs={6} textAlign="right"><Typography variant="body2">{formatCurrency(totals.subtotal, currencySymbol)}</Typography></Grid>
              {costs.map((c) => (
                <React.Fragment key={c.id}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      {c.name || 'Untitled cost'}{c.amountType === 'percentage' ? ` (${c.value}%):` : ':'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <Typography variant="body2">{formatCurrency(computeCostAmount(c, totals.subtotal), currencySymbol)}</Typography>
                  </Grid>
                </React.Fragment>
              ))}
              <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>
              <Grid item xs={6}><Typography variant="h6">Total:</Typography></Grid>
              <Grid item xs={6} textAlign="right">
                <Typography variant="h6" color="primary">{formatCurrency(totals.total, currencySymbol)}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Letter of Credit */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Letter of Credit</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="LC Number" value={lcNumber}
                onChange={(e) => setLcNumber(e.target.value)} disabled={loading} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth select label="LC Status" value={lcStatus}
                onChange={(e) => setLcStatus(e.target.value as LcStatus)} disabled={loading}>
                {LC_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="LC Opened Date" type="date" value={lcOpenedDate}
                onChange={(e) => setLcOpenedDate(e.target.value)}
                InputLabelProps={{ shrink: true }} disabled={loading} />
            </Grid>
          </Grid>
        </Grid>

        {/* Documents */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Documents</Typography>
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={loading}>
            Upload Documents
            <input ref={fileInputRef} type="file" hidden multiple onChange={handleFilesSelected} />
          </Button>

          {existingDocuments.length > 0 && (
            <Stack spacing={1} sx={{ mt: 2 }}>
              {existingDocuments.map((doc) => (
                <Paper key={doc.id} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>{doc.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{doc.type}</Typography>
                  <Button size="small" href={doc.url} target="_blank" rel="noopener noreferrer">View</Button>
                </Paper>
              ))}
            </Stack>
          )}

          {documentFiles.length > 0 && (
            <Stack spacing={1} sx={{ mt: 2 }}>
              {documentFiles.map((d, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ flex: 2 }} noWrap>{d.file.name}</Typography>
                  <TextField
                    select size="small" label="Type" value={d.type} sx={{ flex: 1, minWidth: 160 }}
                    onChange={(e) => updateStagedDocumentType(index, e.target.value as ImportOrderDocumentType)}
                    disabled={loading}
                  >
                    {DOCUMENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                  <IconButton size="small" color="error" onClick={() => removeStagedDocument(index)} disabled={loading}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}
        </Grid>

        {/* Date & Notes */}
        <Grid item xs={12}>
          <TextField fullWidth label="Expected Delivery Date" type="date" value={expectedDeliveryDate}
            onChange={(e) => setExpectedDeliveryDate(e.target.value)}
            InputLabelProps={{ shrink: true }} disabled={loading} />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label="Internal Notes" multiline rows={2} value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)} disabled={loading} />
        </Grid>
      </Grid>
      </Box>
      </Box>

      {/* Footer — fixed, does not scroll. Same full-width-outer/centered-inner
          pattern as the header, so the action buttons line up with the form
          content above regardless of scrollbar width. */}
      <Box sx={{ flexShrink: 0, px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto', width: '100%' }}>
        <Divider sx={{ mb: 2 }} />
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={() => navigate(-1)} disabled={loading} startIcon={<CancelIcon />}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !selectedSupplier || items.length === 0}
            startIcon={<SaveIcon />}
          >
            {isEdit ? 'Update Import Order' : 'Create Import Order'}
          </Button>
        </Stack>
      </Box>

      <SupplierDialog
        open={supplierDialogOpen}
        onClose={() => setSupplierDialogOpen(false)}
        onSuccess={handleSupplierDialogSuccess}
        onShowToast={showToast}
      />

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast(t => ({ ...t, open: false }))}
      />

      {/* ── Full-screen saving overlay ─────────────────────────────────────── */}
      <Backdrop
        open={loading}
        sx={{ zIndex: (t) => t.zIndex.drawer + 200, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
      >
        <Box sx={{
          bgcolor: 'background.paper', borderRadius: 3, px: 5, py: 4,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5,
          minWidth: 280, boxShadow: 24,
        }}>
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              {isEdit ? 'Updating Import Order…' : 'Creating Import Order…'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>
    </Box>
  );
};

export default ImportOrderFormPage;
